import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ReactComponent as ProfileExtraBackground } from './profile-extra-background.svg';
import useApp from './AppContext';
import { callApi, ErrorAlert, defaultRootColors, SuccessAlert } from './crud/utils';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import './Login.css';
import { Link } from 'react-router-dom';
import {
    useNavigate,
    useLocation
} from "react-router-dom";
import Loading from './Loading';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import ContrastIcon from '@mui/icons-material/Contrast';
import { useWebSocket } from './WebSocketContext';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { t, getAvailableLanguages, getCultureInfo } from './i18n';
import { normalizeLanguageCode } from './i18n/translations';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ColorizeIcon from '@mui/icons-material/Colorize';
import { MuiColorInput } from 'mui-color-input' //npm install mui-color-input --legacy-peer-deps

function Login() {
    const app = useApp();
    const ws = useWebSocket();
    const [loginName, setLoginName] = useState("");
    const [password, setPassword] = useState("");
    const [role] = useState(app.sessionToken?.Role);
    const [showWarehouseModal, setShowWarehouseModal] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isBtnActiveFlg, setIsBtnActiveFlg] = useState(true);
    let navigate = useNavigate();
    let location = useLocation();
    const [listUrls, setListUrls] = useState([]);
    const [resetAnimating, setResetAnimating] = useState({});
    const [colors, setColors] = useState({});
    const [logoUrl, setLogoUrl] = useState(null);
    const [isJiggling, setIsJiggling] = useState(false);
    const [isEditingLogo, setIsEditingLogo] = useState(false);
    const [isEditingCompanyName, setIsEditingCompanyName] = useState(false);
    const [selectedPaletteKey, setSelectedPaletteKey] = useState(null);
    const [colorInputErrors, setColorInputErrors] = useState({});
    const logoInputRef = useRef(null);
    const fileDialogOpenRef = useRef(false);

    const toggleJiggle = () => {
        setIsJiggling(prev => !prev);
    };

    let from = location.state?.from?.pathname || "/";

    const [language, setLanguage] = useState(() => {
        const stored = localStorage.getItem("preferredLanguage");
        if (stored) return stored;

        const tokenStr = sessionStorage.getItem("sessionToken");
        if (tokenStr) {
            try {
                const tokenObj = JSON.parse(tokenStr);
                if (tokenObj && typeof tokenObj === "object") {
                    return tokenObj.Language || "el";
                }
            } catch (e) {
                console.error("Invalid sessionToken JSON:", e);
            }
        }
        return "el";
    });

    const [companyName, setCompanyName] = useState(() => {
        const parsed = app.sessionToken?.CustomStyle ? JSON.parse(app.sessionToken.CustomStyle) : {};
        return parsed.CompanyName || t('profile.companyName', language);
    });

    const colorVars = Object.entries(defaultRootColors).map(([name]) => ({
        name,
        label: t(`colors.${name}`, language)
    }));

    // simple UUIDv4 generator (no external deps)
    const generateUUIDv4 = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    };

    const handleLogoUpload = async (e) => {
        if (!e.target.files[0]) return;
        const originalFile = e.target.files[0];
        const nameParts = originalFile.name.split('.');
        const extension = nameParts.length > 1 ? '.' + nameParts.pop() : '';
        const safeFileName = `${generateUUIDv4()}${extension}`;
        let fileToUpload;
        try {
            fileToUpload = new File([originalFile], safeFileName, { type: originalFile.type });
        } catch (err) {
            console.warn('File constructor not available, falling back to original file name', err);
            fileToUpload = originalFile;
        }

        const formData = new FormData();
        formData.append("file", fileToUpload, fileToUpload.name);

        try {
            const res = await fetch(`${app.serverUrl}/api/image/upload`, {
                method: "POST",
                body: formData
            });

            if (!res.ok) {
                setError(`Upload failed: ${res.statusText}`);
            }
            const uploadedPath = `/uploadedImages/${fileToUpload.name}`;
            console.log("✅ Uploaded:", uploadedPath);

            setLogoUrl(uploadedPath);
            const img = document.querySelector(".company-logo");
            if (img) img.src = `${app.serverUrl}${uploadedPath}`;
        } catch (err) {
            console.error("❌ Upload error:", err);
        }
    };

    const saveCustomColorsToStorage = async (customColors) => {
        const parsed = JSON.parse(app.sessionToken?.CustomStyle || '{}');
        const logoPath = logoUrl || parsed.Logo || null;

        const cleanDiff = {};
        Object.entries(customColors).forEach(([key, val]) => {
            if (val !== defaultRootColors[key]) {
                cleanDiff[key] = val;
            }
        });

        const body = {
            CustomStyle: Object.keys(cleanDiff).length > 0 ? cleanDiff : {},
            Logo: logoPath,
            CompanyName: companyName || parsed.CompanyName || null
        };

        try {
            const res = await callApi("POST", `${app.serverUrl}/api/setcustomstyle`, null, body);
            if (res.success) {
                setSuccessMessage(t("profile.customStylesSaved", language));
                setTimeout(() => setSuccessMessage(null), 3500);
                setIsJiggling(false);
                const updatedToken = {
                    ...app.sessionToken,
                    CustomStyle: JSON.stringify(body)
                };
                sessionStorage.setItem("sessionToken", JSON.stringify(updatedToken));
            } else {
                console.error("❌ Failed to save custom styles");
            }
        } catch (err) {
            console.error("Error saving custom styles:", err);
        }
    };

    const handleColorChange = (varName, value) => {
        const updatedColors = { ...colors, [varName]: value };
        document.documentElement.style.setProperty(varName, value);
        setColors(updatedColors);
    };

    const handleEyeDropper = async (colorVarName) => {
        if (!window.EyeDropper) {
            alert('EyeDropper API is not supported in your browser. Please use Chrome or Edge.');
            return;
        }

        try {
            const eyeDropper = new window.EyeDropper();
            const result = await eyeDropper.open();
            handleColorChange(colorVarName, result.sRGBHex);
        } catch (error) {
            console.log('EyeDropper cancelled or failed:', error);
        }
    };

    const resetSingleColor = (colorVarName) => {
        setResetAnimating(prev => ({ ...prev, [colorVarName]: true }));

        const updatedColors = { ...colors };
        delete updatedColors[colorVarName];
        document.documentElement.style.setProperty(colorVarName, defaultRootColors[colorVarName]);
        setColors(updatedColors);
        const isCustomLeft = Object.entries(updatedColors).some(
            ([key, val]) => val !== defaultRootColors[key]
        );

        if (isCustomLeft) {
            saveCustomColorsToStorage(updatedColors);
        } else {
            const parsed = JSON.parse(app.sessionToken?.CustomStyle || '{}');
            saveCustomColorsToStorage({ Logo: parsed.Logo || null });
        }

        setTimeout(() => {
            setResetAnimating(prev => ({ ...prev, [colorVarName]: false }));
        }, 500);
    };

    const themePalettes = useMemo(() => {
        const AberonTheme = {
            '--color-main-background': "#B6265F",
            '--color-secondary-background': "#e3a8c0",
            '--color-secondary-dark-background': "#64394a",
            '--color-shadow-background': "#B6265F40",
            '--color-capacity-background': "#fae6ee",
            '--color-capacity-full-background': "#e6b5c8dc"
        };
        const entersoftoneTheme = {
            "--color-main-background": "#0B5ED7",
            "--color-secondary-background": "#D6E4FF",
            "--color-secondary-dark-background": "#0A2E6D",
            "--color-shadow-background": " #0B5ED740",
            "--color-capacity-background": " #EEF3FF",
            "--color-capacity-full-background": "#B6CCFF"
        };
        const autumnSpiceTheme = {
            '--color-main-background': '#f8791e',
            '--color-secondary-background': '#fbd5b8',
            '--color-secondary-dark-background': '#7a4a2d',
            '--color-shadow-background': '#f8791e40',
            '--color-capacity-background': '#fff1e6',
            '--color-capacity-full-background': '#f8c79fdc'
        };
        const forestTheme = {
            '--color-main-background': '#157347',
            '--color-secondary-background': '#daf5e6',
            '--color-secondary-dark-background': '#0a4f2b',
            '--color-shadow-background': '#15734740',
            '--color-capacity-background': '#eefbf3',
            '--color-capacity-full-background': '#b9f0c9'
        };
        const lavenderTheme = {
            '--color-main-background': '#6d28d9',
            '--color-secondary-background': '#efe6ff',
            '--color-secondary-dark-background': '#3b0d86',
            '--color-shadow-background': '#6d28d940',
            '--color-capacity-background': '#fbf7ff',
            '--color-capacity-full-background': '#cdb4ff'
        };

        return [
            { key: 'aberon', label: t('profile.themes.AberonYardTheme', language), theme: AberonTheme },
            { key: 'entersoftone', label: "EntersoftOne", theme: entersoftoneTheme },
            { key: 'AutumnSpice', label: t('profile.themes.autumnSpice', language), theme: autumnSpiceTheme },
            { key: 'forest', label: t('profile.themes.forest', language), theme: forestTheme },
            { key: 'lavender', label: t('profile.themes.lavender', language), theme: lavenderTheme }
        ];
    }, [language]);

    const applyThemeColors = useCallback((theme) => {
        Object.entries(theme).forEach(([varName, value]) => {
            document.documentElement.style.setProperty(varName, value);
        });
        setColors(prev => ({ ...prev, ...theme }));
        const found = themePalettes?.find(p => p.theme === theme);
        setSelectedPaletteKey(found ? found.key : null);
    }, [themePalettes]);

    useEffect(() => {
        try {
            const parsed = app.sessionToken?.CustomStyle ? JSON.parse(app.sessionToken.CustomStyle) : {};
            const customStyle = parsed.CustomStyle || {};

            if (customStyle && Object.keys(customStyle).length > 0) {
                const match = themePalettes.find(p =>
                    Object.entries(p.theme).every(([k, v]) => customStyle[k] === v)
                );

                if (match) {
                    setSelectedPaletteKey(match.key);
                    applyThemeColors(match.theme);
                } else {
                    setSelectedPaletteKey(null);
                    Object.entries(customStyle).forEach(([k, v]) => {
                        document.documentElement.style.setProperty(k, v);
                    });
                    setColors(prev => ({ ...prev, ...customStyle }));
                }
            } else {
                const aberon = themePalettes.find(p => p.key === 'aberon') || themePalettes[0];
                if (aberon) {
                    setSelectedPaletteKey(aberon.key);
                    applyThemeColors(aberon.theme);
                }
            }
        } catch (e) {
            console.error("Error applying saved custom style:", e);
            const aberon = themePalettes.find(p => p.key === 'aberon') || themePalettes[0];
            if (aberon) {
                setSelectedPaletteKey(aberon.key);
                applyThemeColors(aberon.theme);
            }
        }
    }, [app.sessionToken?.CustomStyle, language, themePalettes, applyThemeColors]);

    useEffect(() => {
        if (listUrls.length === 0) {
            fetch("./config.json")
                .then(res => {
                    if (!res.ok) throw new Error("Failed to load config.json");
                    return res.json();
                })
                .then(data => {
                    if (!data || !data.dataNeed || !Array.isArray(data.dataNeed)) {
                        throw new Error("Invalid JSON structure in config.json");
                    }
                    setListUrls(data.dataNeed);
                    const serverUrlObj = data.dataNeed.find(item => item.ServerUrl);
                    const gridTopObj = data.dataNeed.find(item => item.gridTop);
                    if (serverUrlObj) localStorage.setItem("serverUrl", serverUrlObj.ServerUrl);
                    if (gridTopObj) localStorage.setItem("gridTop", gridTopObj.gridTop);
                })
                .catch(err => setError(`Config load error: ${err.message}`));
        }
    }, [listUrls]);

    const handleThemeChange = (event) => {
        const newTheme = event.target.checked ? 'dark' : 'light';
        app.setTheme(newTheme);
    };

    async function requestLogin() {
        setIsBtnActiveFlg(false);
        if (!loginName) return;

        const url = `${app.serverUrl || localStorage.getItem("serverUrl")}/api/Login?loginName=${loginName}&password=${password}`;

        try {
            const res = await callApi('POST', url);
            if (!res.success || !res.sessionToken) {
                setError("Invalid user name or password");
                setIsBtnActiveFlg(true);
                return;
            }

            if (res.sessionToken.ResetPasswords === "1") {
                app.setSessionToken(res.sessionToken);
                navigate("/change-password", { replace: true });
                return;
            }
            if (res.sessionToken.WarehouseId !== null && res.sessionToken.WarehouseId !== "") {
                // console.log("You have one warehouse");

                finalizeLogin(res);
            } else if (Array.isArray(res.sessionToken.WarehouseId)) {
                // console.log("WarehouseId is an array:", res.sessionToken.WarehouseId);
                setShowWarehouseModal(true);
                setIsBtnActiveFlg(true);
            } else {
                finalizeLogin(res);
            }
        } catch (error) {
            setError(error.message.replace("&password=" + password, ""));
            setIsBtnActiveFlg(true);
        }
    }

    const { fetchAndSetCustomStyle } = useApp();

    const customStyleFetchedRef = useRef(false);

    if (!customStyleFetchedRef.current) {
        fetchAndSetCustomStyle();
        customStyleFetchedRef.current = true;
    }

    function logout() {
        app.setUser(null);
        app.setSessionToken(null);
        navigate("/");



    }

    const handleLanguageChange = (langCode) => {
        const normalizedLang = normalizeLanguageCode(langCode);
        setLanguage(normalizedLang);

        localStorage.setItem("preferredLanguage", normalizedLang);

        if (app.sessionToken) {
            const updatedToken = {
                ...app.sessionToken,
                Language: normalizedLang,
                Culture: getCultureInfo(normalizedLang)
            };
            app.setSessionToken(updatedToken);
            sessionStorage.setItem("sessionToken", JSON.stringify(updatedToken));
        }
    };

    const getEffectiveColor = (name) => {
        try {
            if (colors[name]) return colors[name];
            const sessionToken = JSON.parse(sessionStorage.getItem('sessionToken') || '{}');
            const parsed = JSON.parse(sessionToken?.CustomStyle || '{}');
            const customStyle = parsed.CustomStyle || {};
            return customStyle[name] || defaultRootColors[name];
        } catch {
            return defaultRootColors[name];
        }
    };

    function whenLoggedOff() {
        const parsedCustomStyle = app.sessionToken?.CustomStyle
            ? JSON.parse(app.sessionToken.CustomStyle)
            : {};

        const logoPath = parsedCustomStyle.Logo
            ? `${app.serverUrl}${parsedCustomStyle.Logo}`
            : 'images/Logo-Yard.png';

        return (
            <>
                <div className='container-for-all-login'>
                    <img
                        style={{ height: "200px" }}
                        src={logoPath}
                        alt='Company-Logo'
                    />
                    <Form onSubmit={(e) => { e.preventDefault() }} style={{ background: app.theme === "dark" ? "#4e4e4eff" : "white", color: app.theme === "dark" ? "white " : "black" }}>
                        <h4>{t('login.title', language)}</h4>
                        <Form.Group className="mb-3" controlId="formBasicEmail">
                            <Form.Label>{t('login.emailLabel', language)} <b>*</b></Form.Label>
                            <Form.Control onChange={(e) => setLoginName(e.target.value)} value={loginName} placeholder={t('login.emailPlaceholder', language)} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formBasicPassword">
                            <Form.Label>{t('login.passwordLabel', language)} <b>*</b></Form.Label>
                            <Form.Control onChange={(e) => setPassword(e.target.value)} value={password} type="password" placeholder={t('login.passwordPlaceholder', language)} />
                        </Form.Group>
                        <Button onClick={() => requestLogin()} variant="primary" type="submit">
                            {t('login.submit', language)}
                        </Button>

                        <span className='forgot-password'>
                            <Link to='/forgot-password'>
                                {t('login.forgotPassword', language)}
                            </Link>
                        </span>

                        <div className='separate-section' style={{ background: "var(--bg-fourth)" }}>
                            <span style={{ background: app.theme === "dark" ? "#4e4e4eff" : "white", color: "var(--bg-fourth)" }}>Ή</span>
                        </div>

                        <div className='sign-up-prompt'>
                            <span className='Sing-Up' style={{ color: app.theme === "dark" ? "white  !important " : "black  !important " }}> {t('login.needAccount', language)}&nbsp;   <Link to='/sign-up'><u>{t('login.signUpHere', language)}</u></Link></span>
                        </div>

                        <div className='separate-section' style={{ background: "var(--bg-fourth)" }}>
                        </div>
                        <Form.Group className="mb-3 mt-4 language-select">
                            <Form.Label>{t('login.selectLanguage', language)}</Form.Label>
                            <div className="d-flex flex-wrap gap-3 w-100 justify-content-center">
                                {getAvailableLanguages().map((lang) => {
                                    const isSelected = language === lang.code;
                                    const isDark = app.theme === "dark";
                                    return (
                                        <div
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            className={`language-flag ${isSelected ? "selected" : ""}`}
                                            style={{
                                                background: isSelected ? (isDark ? "white" : "#e9e9e9af") : (isDark ? "#212529" : "white"),
                                                color: isSelected ? (isDark ? "#212529" : "#212529") : (isDark ? "white" : "#212529"),
                                                cursor: 'pointer',
                                                borderRadius: 8,
                                                padding: '6px 12px',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <img src={lang.flag} alt={lang.name} className="flag-icon" />
                                            {lang.name}
                                        </div>
                                    );
                                })}
                            </div>
                        </Form.Group>
                    </Form >

                </div >
                <img src={app.theme === "light" ? '/images/Entersoft-Logo-Login-light.png' : '/images/Entersoft-Logo-Login-dark.png'} alt='Logo-Entersoft' className='Entersoft-Logo-Login' />
                {logoPath !== 'images/Logo-Yard.png' && <img src={app.theme === "light" ? '/images/Aberon-Logo-Login-light.png' : '/images/Aberon-Logo-Login-dark.png'} alt='Logo-Aberon' className='Aberon-Logo-Login' />}
                {!isBtnActiveFlg && <Loading />}
            </>)
    }

    function whenLoggedOn() {

        const parsedCustomStyle = app.sessionToken?.CustomStyle
            ? JSON.parse(app.sessionToken.CustomStyle)
            : {};

        const logoPath = parsedCustomStyle.Logo
            ? `${app.serverUrl}${parsedCustomStyle.Logo}`
            : '/images/Entersoft-Softone-Logo-Login.png';

        return (
            <>
                <div className="container-for-all-profile">
                    <div className="profile-header">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('profile.title', language)}</h1>
                            <p className="text-gray-500">{t('profile.description', language)}</p>
                        </div>
                        <div className='toolkit-profile-header'>
                            {role === 'Admin' && (
                                <>
                                    {isJiggling && <>
                                        <button
                                            className="save-allColor-btn"
                                            onClick={() => saveCustomColorsToStorage(colors)}
                                            title={t('common.saveButton', language)}
                                        >
                                            <SaveIcon />
                                        </button></>}
                                    <button
                                        className="save-allColor-btn"
                                        onClick={toggleJiggle}
                                        title={isJiggling ? `${t("profile.themes.stopJiggle", language)}` : `${t('contextMenu.editable', language)}`}
                                    >
                                        {isJiggling ? <CancelIcon /> : <EditIcon />}
                                    </button>
                                </>
                            )}
                            <button className="logout-btn" onClick={logout}>
                                <ExitToAppOutlinedIcon sx={{ marginRight: 0.25 }} />{t('profile.logout', language)}
                            </button>
                        </div>
                    </div>

                    <div className="profile-page">
                        <div className={`profile-card ${app.theme === 'dark' ? 'DarkThemeGradient' : ''}`}>
                            <div className="avatar-container">
                                <span >
                                    {app.user?.name
                                        ?.trim()
                                        .split(/\s+/)
                                        .map(word => word.charAt(0).toUpperCase())
                                        .join('')
                                        || 'U'}
                                </span>
                            </div>
                            <div className="user-info">
                                <h2 className={`user-name ${app.theme === 'dark' ? 'DarkThemeFont' : ''}`}>{app.user.name}</h2>
                                <span className="user-role"> {app.sessionToken.Role}</span>
                                <div className={`container-profile-info ${isJiggling ? 'editMode' : ''}`}>
                                    <div>
                                        <AccountCircleOutlinedIcon sx={{ marginRight: 1, color: "var(--color-main-background);" }} />
                                        {app.user.name}
                                    </div>
                                    <div>
                                        <EmailOutlinedIcon sx={{ marginRight: 1, color: "var(--color-main-background);" }} />
                                        {app.user.loginName}
                                    </div>

                                </div>
                                <div className="company-container">
                                    <div className="company-header">
                                        <h3>{t('profile.company', language)}</h3>
                                        <div
                                            className={`editable-company-name ${(isJiggling && !isEditingCompanyName) ? 'jiggle' : ''}`}
                                            style={{ display: 'flex', alignItems: 'center', gap: '5px', position: 'relative' }}
                                        >
                                            {isEditingCompanyName ? (
                                                <input
                                                    type="text"
                                                    value={companyName}
                                                    className='company-name-input'
                                                    onChange={(e) => setCompanyName(e.target.value)}
                                                    onBlur={() => setIsEditingCompanyName(false)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') setIsEditingCompanyName(false) }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <>
                                                    <span
                                                        className="company-name-text"
                                                        onClick={() => isJiggling && setIsEditingCompanyName(true)}
                                                        style={{ cursor: isJiggling ? 'pointer' : 'default' }}
                                                    >
                                                        {companyName}
                                                    </span>
                                                    {isJiggling &&
                                                        <EditIcon
                                                            className="edit-icon"
                                                            fontSize="small"
                                                            onClick={() => setIsEditingCompanyName(true)}
                                                        />}
                                                </>
                                            )}
                                        </div>

                                    </div>

                                    <div className={`company-logo-wrapper ${(isJiggling && !isEditingLogo) ? 'jiggle' : ''}`}>
                                        <img
                                            src={logoPath}
                                            alt={t('profile.companyLogoAlt', language)}
                                            className="company-logo"
                                        />
                                        <input
                                            id="logo-upload"
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            style={{ display: "none" }}
                                            onClick={(e) => { e.target.value = null; }}
                                            onChange={async (e) => {
                                                setIsEditingLogo(false);
                                                if (!e.target.files[0]) return;
                                                await handleLogoUpload(e);
                                            }}
                                            onBlur={() => {
                                                setIsEditingLogo(false);
                                            }}
                                        />
                                        {isJiggling &&
                                            <label
                                                htmlFor="logo-upload"
                                                className="company-logo-overlay"
                                                onMouseDown={(ev) => {
                                                    setIsEditingLogo(true);
                                                    fileDialogOpenRef.current = true;
                                                    const onWindowFocus = () => {
                                                        if (fileDialogOpenRef.current) {
                                                            fileDialogOpenRef.current = false;
                                                            setIsEditingLogo(false);
                                                            window.removeEventListener('focus', onWindowFocus);
                                                        }
                                                    };
                                                    window.addEventListener('focus', onWindowFocus, { once: true });
                                                }}
                                            >
                                                <span className="overlay-icon"><EditIcon /></span>
                                            </label>}
                                    </div>
                                </div>

                                <Form.Group
                                    className={`mb-3 mt-3 language-select ${isJiggling ? 'editMode' : ''}`}
                                    style={{ textAlign: "left" }}
                                >
                                    <Form.Label>{t('login.selectLanguage', language)}</Form.Label>
                                    <div className="d-flex flex-wrap gap-3 w-100">
                                        {getAvailableLanguages().map((lang) => {
                                            const isSelected = language === lang.code;
                                            const isDark = app.theme === "dark";
                                            return (
                                                <div
                                                    key={lang.code}
                                                    onClick={() => handleLanguageChange(lang.code)}
                                                    className={`language-flag ${isSelected ? "selected" : ""}`}
                                                    style={{
                                                        background: isSelected ? (isDark ? "white" : "#e9e9e9af") : (isDark ? "#212529" : "white"),
                                                        color: isSelected ? (isDark ? "#212529" : "#212529") : (isDark ? "white" : "#212529"),
                                                        cursor: 'pointer',
                                                        borderRadius: 8,
                                                        padding: '6px 12px',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    <img src={lang.flag} alt={lang.name} className="flag-icon" />
                                                    {lang.name}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Form.Group>

                                <ProfileExtraBackground />
                            </div>
                        </div>


                        <div className={`about-section  ${app.theme === 'dark' ? 'DarkThemeforDark' : ''}`}>
                            <div><h3>{t('profile.aboutTitle', language)}</h3></div>
                            <div className={"container-preferences m-2"}>
                                <h6 className={`${isJiggling ? 'editMode' : ''}`}>{t('profile.appearance', language)}</h6>
                                <div className={`form-dark-mode ${isJiggling ? 'editMode' : ''}`}>
                                    <span><ContrastIcon style={{ marginRight: "5px" }} /> {t(app.theme === 'dark' ? 'profile.darkTheme' : 'profile.lightTheme', language)}</span>
                                    <label className="switch form-check-label" htmlFor="flexSwitchCheckChecked">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            id="flexSwitchCheckChecked"
                                            checked={app.theme === 'dark'}
                                            onChange={handleThemeChange}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                                <>
                                    <div className='border-top-custom'></div>
                                    <div className="color-picker-section-enhanced">
                                        <div className="section-header">
                                            <h6>{t('profile.colorSelection', language)}</h6>
                                            {isJiggling && (
                                                <span className="edit-hint">{t('profile.clickToCustomize', language) || 'Click colors to customize'}</span>
                                            )}
                                        </div>

                                        {colorVars.map((colorVar) => (
                                            <div key={colorVar.name} className={`color-picker-row-enhanced ${isJiggling ? 'editable' : ''}`}>
                                                <div className="color-info">
                                                    <label className="color-label">{colorVar.label}</label>
                                                    <span className="color-value">{getEffectiveColor(colorVar.name)}</span>
                                                </div>

                                                <div className="color-controls">
                                                    <div className={`color-button-group ${isJiggling ? 'active' : ''}`}>
                                                        {isJiggling && (
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type="text"
                                                                    value={getEffectiveColor(colorVar.name)}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        if (/^#[0-9A-Fa-f]{0,8}$/.test(val) || val === '#') {
                                                                            handleColorChange(colorVar.name, val);
                                                                            setColorInputErrors(prev => ({ ...prev, [colorVar.name]: null }));
                                                                        } else {
                                                                            setColorInputErrors(prev => ({
                                                                                ...prev,
                                                                                [colorVar.name]: language === 'el'
                                                                                    ? 'Μόνο hex (#RRGGBB ή #RRGGBBAA)'
                                                                                    : 'Only hex (#RRGGBB or #RRGGBBAA)'
                                                                            }));
                                                                        }
                                                                    }}
                                                                    placeholder="#FFFFFF"
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        fontSize: '13px',
                                                                        fontFamily: 'monospace',
                                                                        fontWeight: '600',
                                                                        textTransform: 'uppercase',
                                                                        border: `2px solid ${colorInputErrors[colorVar.name] ? '#dc3545' : (app.theme === 'dark' ? '#505050' : '#c0c0c0')}`,
                                                                        borderRadius: '22px',
                                                                        background: app.theme === 'dark' ? '#1a1a1a' : '#f8f8f8',
                                                                        color: app.theme === 'dark' ? '#ffffff' : '#000000',
                                                                        width: '90px',
                                                                        outline: 'none',
                                                                        transition: 'border-color 0.2s',
                                                                    }}
                                                                    onFocus={(e) => {
                                                                        if (!colorInputErrors[colorVar.name]) {
                                                                            e.target.style.borderColor = 'var(--color-main-background)';
                                                                        }
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (!colorInputErrors[colorVar.name]) {
                                                                            e.target.style.borderColor = app.theme === 'dark' ? '#505050' : '#c0c0c0';
                                                                        }
                                                                    }}
                                                                />
                                                                {colorInputErrors[colorVar.name] && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        top: '100%',
                                                                        left: 0,
                                                                        fontSize: '10px',
                                                                        color: '#dc3545',
                                                                        marginTop: '2px',
                                                                        whiteSpace: 'nowrap',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {colorInputErrors[colorVar.name]}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        <MuiColorInput
                                                            value={getEffectiveColor(colorVar.name)}
                                                            onChange={(newColor) => handleColorChange(colorVar.name, newColor)}
                                                            disabled={!isJiggling}
                                                            format="hex8"
                                                            size="small"
                                                            sx={{
                                                                '& .MuiInputBase-input': {
                                                                    padding: 0,
                                                                    cursor: isJiggling ? 'pointer' : 'default',
                                                                    textIndent: '-9999px',
                                                                    width: 0
                                                                },
                                                                '& .MuiInputAdornment-root': {
                                                                    margin: 0
                                                                },
                                                                '& .MuiColorInput-Button': {
                                                                    width: '30px',
                                                                    height: '30px',
                                                                    borderRadius: '50%',
                                                                    padding: 0,
                                                                    minWidth: 0,
                                                                    border: isJiggling ? '2px solid rgba(0,0,0,0.1)' : '2px solid rgba(0,0,0,0.05)',
                                                                    transition: 'all 0.2s ease',
                                                                    boxShadow: isJiggling ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                                                                    '&:hover': {
                                                                        transform: isJiggling ? 'scale(1.05)' : 'none',
                                                                        boxShadow: isJiggling ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                                                                    }
                                                                },
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    border: 'none'
                                                                }
                                                            }}
                                                            PopoverProps={{
                                                                sx: {
                                                                    '& .MuiPopover-paper': {
                                                                        borderRadius: '16px',
                                                                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                                                                        overflow: 'hidden',
                                                                        padding: '20px',
                                                                        background: app.theme === 'dark' ? '#2d2d2d' : '#ffffff',
                                                                    },
                                                                    '& .muicc-pointer': {
                                                                        width: '22px',
                                                                        height: '22px',
                                                                        borderRadius: '50%',
                                                                        border: '3px solid white',
                                                                        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25)',
                                                                    },
                                                                    '& .muicc-hue-horizontal': {
                                                                        borderRadius: '12px',
                                                                        height: '14px',
                                                                    },
                                                                    '& .muicc-alpha-horizontal': {
                                                                        borderRadius: '12px',
                                                                        height: '14px',
                                                                    },
                                                                    '& .muicc-hue-pointer, & .muicc-alpha-pointer': {
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        border: '3px solid white',
                                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                                                                    },
                                                                    '& .MuiTextField-root': {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            borderRadius: '12px',
                                                                            background: app.theme === 'dark' ? '#1a1a1a' : '#f8f8f8',
                                                                            '& fieldset': {
                                                                                borderColor: 'transparent',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: 'var(--color-main-background)',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: 'var(--color-main-background)',
                                                                                borderWidth: '2px',
                                                                            },
                                                                        },
                                                                    },
                                                                    '& .muicc-colors-grid': {
                                                                        gap: '10px',
                                                                        '& > div': {
                                                                            borderRadius: '10px',
                                                                            transition: 'all 0.2s ease',
                                                                            '&:hover': {
                                                                                transform: 'scale(1.15)',
                                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                            },
                                                                        },
                                                                    },
                                                                }
                                                            }}
                                                        />

                                                        {isJiggling && (
                                                            <div className="action-buttons">
                                                                {window.EyeDropper && (
                                                                    <button
                                                                        className="eye-dropper-btn"
                                                                        onClick={() => handleEyeDropper(colorVar.name)}
                                                                        title="Pick color from screen"
                                                                    >
                                                                        <ColorizeIcon fontSize="small" />
                                                                    </button>
                                                                )}

                                                                {(colors[colorVar.name] && colors[colorVar.name] !== defaultRootColors[colorVar.name]) && (
                                                                    <button
                                                                        className={`reset-btn ${resetAnimating[colorVar.name] ? 'animating' : ''}`}
                                                                        title={t('profile.resetColor', language)}
                                                                        onClick={() => resetSingleColor(colorVar.name)}
                                                                    >
                                                                        <RestartAltIcon fontSize="small" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {isJiggling &&
                                        <div className='container-pallet-reset'>
                                            <div className="preset-theme-section">
                                                <span className="brand-name"> {t('profile.themes.title', language)}:</span>
                                                <div className="theme-palettes-wrapper" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                                                    {themePalettes.map((p) => (
                                                        <div key={p.key} style={{ textAlign: 'center', position: 'relative' }}>
                                                            {selectedPaletteKey === p.key && (
                                                                <CheckIcon
                                                                    fontSize="small"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        left: '50%',
                                                                        top: '50%',
                                                                        transform: 'translate(-50%, -50%)',
                                                                        zIndex: 55,
                                                                        color: 'var(--color-main-background)',
                                                                        border: '2px solid var(--color-main-background)',
                                                                        background: 'white',
                                                                        fontSize: '35px',
                                                                        padding: '4px',
                                                                        borderRadius: '22px'
                                                                    }}

                                                                />
                                                            )}
                                                            <div
                                                                className={`theme-palette ${(isJiggling && selectedPaletteKey !== p.key) ? 'jiggle' : ''}`}
                                                                title={p.label}
                                                                onClick={() => {
                                                                    applyThemeColors(p.theme);
                                                                    setSelectedPaletteKey(p.key);
                                                                }}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    gap: 4,
                                                                    padding: 6,
                                                                    borderRadius: 6
                                                                }}
                                                            >
                                                                {Object.values(p.theme).map((color, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        className="theme-color-box"
                                                                        style={{ backgroundColor: color, width: 18, height: 18, borderRadius: 22 }}
                                                                    />
                                                                ))}
                                                            </div>
                                                            <div style={{ fontSize: 12, marginTop: 6 }}>{p.label}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>}
                                </>
                            </div>
                        </div>
                    </div>
                    <div />
                </div >
            </>
        );
    }

    function finalizeLogin(res) {
        setShowWarehouseModal(false);
        res.sessionToken.Language = language;
        handleLanguageChange(language);

        app.setUser({ loginName: loginName, name: res.username });
        app.setSessionToken(res.sessionToken);
        let savedColors = {};
        try {
            const parsed = app.sessionToken?.CustomStyle
                ? JSON.parse(app.sessionToken.CustomStyle)
                : {};
            savedColors = parsed.CustomStyle || {};
        } catch (e) {
            console.warn("Invalid CustomStyle JSON:", e);
            savedColors = {};
        }

        const updatedColors = {};
        Object.entries(defaultRootColors).forEach(([name, defaultVal]) => {
            const colorVal = savedColors[name] || defaultVal;
            document.documentElement.style.setProperty(name, colorVal);
            updatedColors[name] = colorVal;
        });

        setColors(updatedColors);

        const message = {
            Type: "Login",
            Data: res.username,
            UserId: res.sessionToken.UserId,
            WarehouseId: res.sessionToken.WarehouseId || res.sessionToken.WarehouseId[0] || null
        };
        ws.sendByObject(message);
        navigate(from, { replace: true });
    }

    function createContent() {
        if (app.user) {
            return whenLoggedOn();
        } else {
            return whenLoggedOff();
        }
    }
    return <div className="h-100">
        <ErrorAlert message={error} setMessage={setError} language={language} />
        <SuccessAlert message={successMessage} setMessage={setSuccessMessage} language={language} />
        {createContent()}
        <Modal show={showWarehouseModal} onHide={() => setShowWarehouseModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('warehouseModal.title', language)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{t('warehouseModal.description', language)}</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowWarehouseModal(false)}>
                    {t('warehouseModal.cancel', language)}
                </Button>
                <Button variant="primary" onClick={() => finalizeLogin()}>
                    {t('warehouseModal.continue', language)}
                </Button>
            </Modal.Footer>
        </Modal>
    </div >
}

export default Login;